import React, { useState } from 'react';
import { BaseComponent } from '../types/protocols';
import { translate } from '../types/EnumTranslations';
import { HazardType, HazardousState } from '../types/Enums';
import "./WarningViewComponent.css";

interface HazardComponentInfo {
    key: string;
    type: string;
    label?: string;
    specificType?: string;
    hazardState: string;
}

interface HazardData {
    hazardType: string;
    componentCount: number;
    components: HazardComponentInfo[];
}

interface NestedHazardViewProps {
    hazards: HazardData[];
    buildingKey: string;
}

const AlertIcon: React.FC<{ size: string }> = ({ size }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
);

const NestedHazardView: React.FC<NestedHazardViewProps> = ({ 
    hazards, 
    buildingKey 
}) => {
    const [isMainExpanded, setIsMainExpanded] = useState(false);
    const [expandedHazards, setExpandedHazards] = useState<Set<string>>(new Set());

    const handleComponentClick = (component: HazardComponentInfo) => {
        // Open the component page in a new tab
        const url = `/building/${buildingKey}/component/${component.key}`;
        window.open(url, '_blank');
    };

    const getComponentDisplayName = (component: HazardComponentInfo): string => {
        // The label field now contains the component's displayName() result
        return component.label || component.type;
    };

    const toggleHazardExpansion = (hazardKey: string) => {
        const newExpandedHazards = new Set(expandedHazards);
        if (newExpandedHazards.has(hazardKey)) {
            newExpandedHazards.delete(hazardKey);
        } else {
            newExpandedHazards.add(hazardKey);
        }
        setExpandedHazards(newExpandedHazards);
    };

    const totalComponents = hazards.reduce((sum, hazard) => sum + hazard.componentCount, 0);

    return (
        <div className="hazard-section">
            <div className="alert-box alert-box-enhanced cursor-pointer hazard-text-color-red">
                <div
                    className="alert-header"
                    onClick={() => setIsMainExpanded(!isMainExpanded)}
                >
                    <div className="alert-dot alert-dot-red"/>
                    <AlertIcon size="28"/>
                    <h4 className="alert-title hazard-text-color-red">
                        Miljö/hälsofara ({hazards.length})
                    </h4>
                    <svg
                        className={`ml-auto transform transition-transform duration-200 ${isMainExpanded ? 'rotate-180' : ''}`}
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                    >
                        <path d="M6 9l6 6 6-6"/>
                    </svg>
                </div>

                {isMainExpanded && (
                    <div className="alert-content">
                        {hazards.map((hazardData, index) => (
                            <div key={`${hazardData.hazardType}-${index}`} style={{ marginBottom: '8px' }}>
                                {/* Individual hazard header */}
                                <div 
                                    className="enhanced-hazard-item hazard-item"
                                    onClick={() => toggleHazardExpansion(hazardData.hazardType)}
                                    style={{ 
                                        cursor: 'pointer'
                                    }}
                                >
                                    <AlertIcon size="18"/>
                                    <div style={{ flex: 1 }}>
                                        <span className="hazard-text">
                                            {hazardData.hazardType}
                                        </span>
                                        <span style={{ marginLeft: '8px', color: '#666', fontSize: '0.875rem' }}>
                                            ({hazardData.componentCount})
                                        </span>
                                    </div>
                                    <svg
                                        className={`transform transition-transform duration-200 ${expandedHazards.has(hazardData.hazardType) ? 'rotate-180' : ''}`}
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    >
                                        <path d="M6 9l6 6 6-6"/>
                                    </svg>
                                </div>

                                {/* Components list for this hazard */}
                                {expandedHazards.has(hazardData.hazardType) && (
                                    <div style={{ 
                                        marginLeft: '20px', 
                                        marginTop: '8px',
                                        paddingLeft: '12px'
                                    }}>
                                        {hazardData.components.map((component, componentIndex) => (
                                            <div 
                                                key={`${component.key}-${componentIndex}`}
                                                className="enhanced-hazard-item hazard-item"
                                                onClick={() => handleComponentClick(component)}
                                                style={{ 
                                                    cursor: 'pointer',
                                                    marginBottom: '4px',
                                                    backgroundColor: 'white',
                                                    border: '1px solid #e9ecef'
                                                }}
                                            >
                                                <AlertIcon size="16"/>
                                                <div>
                                                    <span className="hazard-text">
                                                        {getComponentDisplayName(component)}
                                                    </span>
                                                    <br/>
                                                    <span className="hazard-location">
                                                        {component.hazardState && `${component.hazardState}`}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default NestedHazardView;
